import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loading } from './Loading';
import { roleAtLeast } from '@/lib/roles';
import type { Role } from '@/types';

/**
 * Gates child routes on the live /api/me identity (Cloudflare Access + D1 role).
 *
 *   loading                  → spinner (first /api/me round-trip not settled)
 *   error / no identity      → /login (Access cookie missing or /api/me failed)
 *   status !== approved      → /pending (signed in via Access, awaiting role / revoked)
 *   requiredRole set & below → /dashboard (insufficient role)
 *   otherwise                → render children
 *
 * RBAC is a strict tier: super_admin > admin > support > read_only. `requiredRole`
 * means "this role or higher". The Access cookie is the real auth boundary; this
 * is the in-SPA role gate that mirrors admin-api's server-side enforcement.
 */
export function ProtectedRoute({
  children,
  requiredRole,
}: {
  children: ReactNode;
  requiredRole?: Role;
}) {
  const { loading, email, role, status } = useAuth();

  if (loading) return <Loading />;
  if (!email) return <Navigate to="/login" replace />;
  if (status !== 'approved' || role === null) return <Navigate to="/pending" replace />;
  if (requiredRole && !roleAtLeast(role, requiredRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
